import { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Filter, Upload, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import AddTariffRateDialog from '../components/tariff/AddTariffRateDialog';
import EditTariffRateDialog from '../components/tariff/EditTariffRateDialog';
import ImportTariffDialog from '../components/tariff/ImportTariffDialog';
import { tariffRatesApi } from '../api/tariffRates';

export default function TariffRates() {
  const [rates, setRates] = useState([]);
  const [stats, setStats] = useState({ total_rates: 0, active_rates: 0, country_pairs: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingRate, setEditingRate] = useState(null);

  useEffect(() => {
    fetchTariffRates();
  }, [searchTerm, countryFilter, activeOnly]);

  const fetchTariffRates = async () => {
    setLoading(true);
    try {
      const response = await tariffRatesApi.getTariffRates(searchTerm, countryFilter, activeOnly);
      if (response.success) {
        setRates(response.data.rates);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching tariff rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rateId) => {
    if (!confirm('Are you sure you want to delete this tariff rate?')) return;
    
    try {
      const response = await tariffRatesApi.deleteTariffRate(rateId);
      if (response.success) {
        fetchTariffRates();
      } else {
        alert(response.error || 'Failed to delete tariff rate');
      }
    } catch (error) {
      console.error('Error deleting tariff rate:', error);
      alert('Failed to delete tariff rate');
    }
  };

  const handleExport = async () => {
    try {
      await tariffRatesApi.exportTariffRates(activeOnly);
    } catch (error) {
      console.error('Error exporting tariff rates:', error);
      alert('Failed to export tariff rates');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tariff Rate Management</h1>
          <p className="text-gray-600 mt-1">Manage tariff rates by country pair and HTS code</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </Button>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Tariff Rate
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Rates</CardDescription>
            <CardTitle className="text-3xl">{stats.total_rates}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Rates</CardDescription>
            <CardTitle className="text-3xl">{stats.active_rates}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Country Pairs</CardDescription>
            <CardTitle className="text-3xl">{stats.country_pairs}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by country, HTS code, notes, or material name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium">Active Rates Only</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tariff Rates Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : rates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tariff rates found. Click "Add Tariff Rate" to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>HTS Code</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Rate (%)</TableHead>
                    <TableHead>Effective Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell className="font-medium">{rate.country_from}</TableCell>
                      <TableCell className="font-medium">{rate.country_to}</TableCell>
                      <TableCell>{rate.hts_code}</TableCell>
                      <TableCell>
                        {rate.material_name ? (
                          <span className="text-sm text-gray-700">{rate.material_name}</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">{rate.rate_percentage}%</TableCell>
                      <TableCell>{formatDate(rate.effective_date)}</TableCell>
                      <TableCell>{formatDate(rate.end_date)}</TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate text-sm text-gray-600">
                          {rate.notes || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {rate.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingRate(rate)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(rate.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <AddTariffRateDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => {
          setShowAddDialog(false);
          fetchTariffRates();
        }}
      />

      {/* Edit Dialog */}
      {editingRate && (
        <EditTariffRateDialog
          open={!!editingRate}
          rate={editingRate}
          onClose={() => setEditingRate(null)}
          onSuccess={() => {
            setEditingRate(null);
            fetchTariffRates();
          }}
        />
      )}

      {/* Import Dialog */}
      <ImportTariffDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={() => {
          setShowImportDialog(false);
          fetchTariffRates();
        }}
      />
    </div>
  );
}
